import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { collection, doc, setDoc, getDocs, query, where, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storageInstance } from '../config/firebase';
import LegalDisclaimer from '../components/LegalDisclaimer';
import { formatPixKey, validatePixKey } from '../utils/currency';

// Generate URL-friendly slug from title
function generateSlug(title) {
  return title
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // remove accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 60);
}

// Ensure slug is unique by appending a short suffix if needed
async function ensureUniqueSlug(baseSlug) {
  const q = query(collection(db, 'raffles'), where('slug', '==', baseSlug), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return baseSlug;
  const suffix = Math.random().toString(36).substring(2, 6);
  return `${baseSlug}-${suffix}`;
}

const MAX_MEDIA = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB per file
const ACCEPTED_TYPES = 'image/*,video/mp4,video/webm,video/quicktime';

export default function CreateRaffle() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const mediaInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    prizeDescription: '',
    prizeValue: '',
    numberType: 'limited',
    maxNumbers: '100',
    ticketPrice: '',
    currency: 'BRL',
    drawDate: '',
    drawTime: '19:00',
    pixKey: '',
    pixKeyType: 'cpf',
  });

  // QR Code image state
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [qrCodePreview, setQrCodePreview] = useState(null);
  const qrCodeInputRef = useRef(null);

  // Media gallery state (up to 3 images/videos)
  const [mediaFiles, setMediaFiles] = useState([]); // [{ file, preview, type: 'image'|'video' }]
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [error, setError] = useState('');
  const [legalAccepted, setLegalAccepted] = useState(false);

  if (!currentUser) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h2>{t('auth.login')}</h2>
          <p>{t('common.error')}</p>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'pixKey') {
      // Accept any input freely — no masking
      setFormData(prev => ({ ...prev, pixKey: value }));
    } else if (name === 'pixKeyType') {
      // Clear the PIX key when type changes to avoid invalid leftovers
      setFormData(prev => ({ ...prev, pixKeyType: value, pixKey: '' }));
      // Clear QR code image if switching away from qrcode
      if (value !== 'qrcode') {
        setQrCodeFile(null);
        setQrCodePreview(null);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // ─── QR Code Handler ───
  const handleQrCodeUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert(t('createRaffle.fileTooLarge'));
      return;
    }
    setQrCodeFile(file);
    setQrCodePreview(URL.createObjectURL(file));
    if (qrCodeInputRef.current) qrCodeInputRef.current.value = '';
  };

  // ─── Media Gallery Handlers ───
  const handleMediaAdd = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const remaining = MAX_MEDIA - mediaFiles.length;
    if (remaining <= 0) {
      alert(t('createRaffle.maxMediaReached'));
      return;
    }

    const toAdd = files.slice(0, remaining);
    const newMedia = [];

    toAdd.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        alert(`${file.name}: ${t('createRaffle.fileTooLarge')}`);
        return;
      }
      const isVideo = file.type.startsWith('video/');
      const preview = URL.createObjectURL(file);
      newMedia.push({ file, preview, type: isVideo ? 'video' : 'image' });
    });

    setMediaFiles(prev => [...prev, ...newMedia]);
    // Reset input so same file can be re-selected
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  };

  const handleMediaRemove = (index) => {
    setMediaFiles(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleMediaReorder = (fromIdx, toIdx) => {
    setMediaFiles(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIdx, 1);
      updated.splice(toIdx, 0, moved);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!formData.title || !formData.description || !formData.ticketPrice || !formData.drawDate) {
        setError(t('createRaffle.fillRequired'));
        setLoading(false);
        return;
      }

      // Validate PIX key (QR Code validated by image upload instead)
      if (formData.pixKeyType === 'qrcode') {
        if (!qrCodeFile) {
          setError(t('createRaffle.pixQrCodeRequired'));
          setLoading(false);
          return;
        }
      } else {
        const pixValidation = validatePixKey(formData.pixKey, formData.pixKeyType);
        if (pixValidation !== true) {
          setError(t(pixValidation));
          setLoading(false);
          return;
        }
      }

      const raffleId = doc(collection(db, 'raffles')).id;
      const baseSlug = generateSlug(formData.title);
      const slug = await ensureUniqueSlug(baseSlug || raffleId);

      // Upload all media files to Firebase Storage
      const mediaUrls = [];
      let imageUrl = ''; // Keep backwards compat — first image is the "main" imageUrl

      for (let i = 0; i < mediaFiles.length; i++) {
        const media = mediaFiles[i];
        setUploadProgress(`Enviando ${i + 1}/${mediaFiles.length}...`);
        try {
          const ext = media.file.name.split('.').pop();
          const fileName = `${i}_${Date.now()}.${ext}`;
          const mediaRef = ref(storageInstance, `raffles/${raffleId}/${fileName}`);
          const snapshot = await uploadBytes(mediaRef, media.file);
          const url = await getDownloadURL(snapshot.ref);
          mediaUrls.push({ url, type: media.type, name: media.file.name });
          if (i === 0 && media.type === 'image') {
            imageUrl = url;
          }
        } catch (uploadErr) {
          console.error('Error uploading media:', uploadErr);
        }
      }

      // If no image was set as main, use the first available image
      if (!imageUrl) {
        const firstImg = mediaUrls.find(m => m.type === 'image');
        if (firstImg) imageUrl = firstImg.url;
      }

      setUploadProgress('');

      // Upload QR Code image if applicable
      let qrCodeUrl = '';
      if (formData.pixKeyType === 'qrcode' && qrCodeFile) {
        setUploadProgress('Enviando QR Code...');
        try {
          const ext = qrCodeFile.name.split('.').pop();
          const qrRef = ref(storageInstance, `raffles/${raffleId}/qrcode_${Date.now()}.${ext}`);
          const snap = await uploadBytes(qrRef, qrCodeFile);
          qrCodeUrl = await getDownloadURL(snap.ref);
        } catch (err) {
          console.error('Error uploading QR Code:', err);
        }
        setUploadProgress('');
      }

      const raffleData = {
        title: formData.title,
        description: formData.description,
        prizeDescription: formData.prizeDescription,
        prizeValue: formData.prizeValue ? parseFloat(formData.prizeValue) : 0,
        numberType: formData.numberType,
        maxNumbers: formData.numberType === 'limited' ? parseInt(formData.maxNumbers) : null,
        zeroBased: true,
        ticketPrice: parseFloat(formData.ticketPrice),
        currency: formData.currency,
        drawDate: `${formData.drawDate}T${formData.drawTime || '19:00'}:00`,
        pixKey: formData.pixKeyType === 'qrcode' ? '' : formData.pixKey,
        pixKeyType: formData.pixKeyType,
        pixQrCodeUrl: qrCodeUrl || '',
        imageUrl: imageUrl,
        mediaGallery: mediaUrls, // NEW: array of {url, type, name}
        slug: slug,
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email,
        createdByEmail: currentUser.email,
        createdAt: new Date().toISOString(),
        status: 'active',
        numbersSold: 0,
        totalRevenue: 0,
        participants: [],
        legalDisclaimerAccepted: true,
        legalDisclaimerAcceptedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'raffles', raffleId), { ...raffleData, id: raffleId });
      console.log('Raffle created with slug:', slug);
      navigate('/my-raffles');
    } catch (err) {
      console.error('Error creating raffle:', err);
      setError(t('createRaffle.createFailed'));
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  return (
    <div className="create-raffle-page">
      <div className="create-raffle-form">
        <h1>{t('raffle.create')}</h1>
        <p className="form-subtitle">{t('hero.subtitle')}</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="form-group">
            <label>{t('raffle.title')} *</label>
            <input type="text" className="form-control" name="title" value={formData.title} onChange={handleInputChange} placeholder="ex: iPhone 15 Pro Max" required />
          </div>

          {/* Draw Date & Time */}
          <div className="form-row">
            <div className="form-group">
              <label>{t('raffle.drawDate')} *</label>
              <input type="date" className="form-control" name="drawDate" value={formData.drawDate} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>{t('raffle.drawTime')} *</label>
              <input type="time" className="form-control" name="drawTime" value={formData.drawTime} onChange={handleInputChange} required />
            </div>
          </div>

          {/* Description with markdown hint */}
          <div className="form-group">
            <label>{t('raffle.description')} *</label>
            <textarea
              className="form-control"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={"Descreva sua campanha e o prêmio...\n\nVocê pode usar formatação:\n**negrito**, *itálico*, - listas"}
              required
              rows={6}
              style={{ fontFamily: 'inherit', resize: 'vertical' }}
            />
            <small style={{ color: 'var(--dark-light)', marginTop: '0.4rem', display: 'block', fontSize: '0.8rem' }}>
              {t('createRaffle.markdownHint')}
            </small>
          </div>

          {/* Prize Details */}
          <div className="form-row">
            <div className="form-group">
              <label>{t('raffle.prize')}</label>
              <input type="text" className="form-control" name="prizeDescription" value={formData.prizeDescription} onChange={handleInputChange} placeholder="ex: MacBook Pro 16 polegadas" />
            </div>
            <div className="form-group">
              <label>{t('raffle.prizeValue')}</label>
              <input type="number" className="form-control" name="prizeValue" value={formData.prizeValue} onChange={handleInputChange} placeholder="3500" />
            </div>
          </div>

          {/* Number Type */}
          <div className="form-group">
            <label>{t('raffle.numberType')} *</label>
            <div className="number-type-toggle">
              <button type="button" className={`type-btn ${formData.numberType === 'limited' ? 'active' : ''}`} onClick={() => setFormData(p => ({ ...p, numberType: 'limited' }))}>
                {t('raffle.limited')}
              </button>
              <button type="button" className={`type-btn ${formData.numberType === 'unlimited' ? 'active' : ''}`} onClick={() => setFormData(p => ({ ...p, numberType: 'unlimited' }))}>
                {t('raffle.unlimited')}
              </button>
            </div>
          </div>

          {formData.numberType === 'limited' && (
            <div className="form-group">
              <label>{t('raffle.maxNumbers')}</label>
              <select className="form-control" name="maxNumbers" value={formData.maxNumbers} onChange={handleInputChange}>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
                <option value="1000">1000</option>
                <option value="5000">5000</option>
              </select>
            </div>
          )}

          {/* Ticket Price & Currency */}
          <div className="form-row">
            <div className="form-group">
              <label>{t('raffle.ticketPrice')} *</label>
              <input type="number" className="form-control" name="ticketPrice" value={formData.ticketPrice} onChange={handleInputChange} placeholder="10.00" step="0.01" required />
            </div>
            <div className="form-group">
              <label>{t('raffle.currency')}</label>
              <div className="currency-toggle">
                <button type="button" className={`currency-btn ${formData.currency === 'BRL' ? 'active' : ''}`} onClick={() => setFormData(p => ({ ...p, currency: 'BRL' }))}>
                  R$ BRL
                </button>
                <button type="button" className={`currency-btn ${formData.currency === 'USD' ? 'active' : ''}`} onClick={() => setFormData(p => ({ ...p, currency: 'USD' }))}>
                  $ USD
                </button>
              </div>
            </div>
          </div>

          {/* PIX Payment Info */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(0,206,201,0.08), rgba(108,92,231,0.08))',
            borderRadius: 'var(--radius-md)', padding: '1.5rem', marginBottom: '1.2rem',
            border: '2px solid rgba(0,206,201,0.2)'
          }}>
            <h3 style={{ fontSize: '1.1rem', marginBottom: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              📲 {t('createRaffle.pixTitle')}
            </h3>
            <p style={{ fontSize: '0.9rem', color: 'var(--dark-light)', marginBottom: '1rem', textAlign: 'justify' }}>
              {t('createRaffle.pixDescription')}
            </p>
            <div className="form-row">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>{t('createRaffle.pixKeyType')}</label>
                <select className="form-control" name="pixKeyType" value={formData.pixKeyType} onChange={handleInputChange}>
                  <option value="cpf">CPF</option>
                  <option value="cnpj">CNPJ</option>
                  <option value="email">Email</option>
                  <option value="phone">{t('createRaffle.pixPhone')}</option>
                  <option value="random">{t('createRaffle.pixRandom')}</option>
                  <option value="copiaecola">{t('createRaffle.pixCopiaECola')}</option>
                  <option value="qrcode">QR Code</option>
                </select>
              </div>

              {/* Conditional: text input for key types, textarea for copia e cola, image upload for QR code */}
              {formData.pixKeyType === 'qrcode' ? (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>{t('createRaffle.pixQrCodeLabel')} *</label>
                  <div style={{
                    border: '2px dashed var(--gray-300)', borderRadius: 'var(--radius-md)',
                    padding: '1.5rem', textAlign: 'center', cursor: 'pointer',
                    background: qrCodePreview ? 'var(--white)' : 'var(--gray-100)',
                    transition: 'all 0.3s',
                  }}
                    onClick={() => qrCodeInputRef.current?.click()}
                  >
                    {qrCodePreview ? (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img src={qrCodePreview} alt="QR Code" style={{
                          maxWidth: '200px', maxHeight: '200px', borderRadius: 'var(--radius-sm)',
                        }} />
                        <button type="button" onClick={(e) => {
                          e.stopPropagation();
                          setQrCodeFile(null);
                          setQrCodePreview(null);
                        }} style={{
                          position: 'absolute', top: '-8px', right: '-8px',
                          background: 'var(--danger)', color: 'white', border: 'none',
                          borderRadius: '50%', width: '24px', height: '24px',
                          cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
                        }}>✕</button>
                      </div>
                    ) : (
                      <>
                        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📷</div>
                        <p style={{ color: 'var(--dark-light)', fontSize: '0.9rem', margin: 0 }}>
                          {t('createRaffle.pixQrCodeHint')}
                        </p>
                      </>
                    )}
                    <input
                      ref={qrCodeInputRef}
                      type="file" accept="image/*"
                      onChange={handleQrCodeUpload}
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              ) : formData.pixKeyType === 'copiaecola' ? (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>{t('createRaffle.pixKey')} *</label>
                  <textarea
                    className="form-control" name="pixKey"
                    value={formData.pixKey} onChange={handleInputChange}
                    placeholder={t('createRaffle.pixCopiaEColaHint')}
                    rows={3}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
                    required
                  />
                </div>
              ) : (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>{t('createRaffle.pixKey')} *</label>
                  <input
                    type="text" className="form-control" name="pixKey"
                    value={formData.pixKey} onChange={handleInputChange}
                    placeholder={
                      formData.pixKeyType === 'cpf' ? '000.000.000-00' :
                      formData.pixKeyType === 'cnpj' ? '00.000.000/0000-00' :
                      formData.pixKeyType === 'email' ? 'voce@email.com' :
                      formData.pixKeyType === 'phone' ? '(11) 99999-9999' :
                      'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
                    }
                    required
                  />
                </div>
              )}
            </div>
          </div>

          {/* ─── MEDIA GALLERY UPLOAD (up to 3 photos/videos) ─── */}
          <div className="form-group">
            <label>{t('createRaffle.mediaGallery')}</label>
            <small style={{ color: 'var(--dark-light)', display: 'block', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
              {t('createRaffle.mediaGalleryHint')}
            </small>

            {/* Thumbnail previews */}
            {mediaFiles.length > 0 && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(mediaFiles.length, 3)}, 1fr)`,
                gap: '0.75rem',
                marginBottom: '0.75rem',
              }}>
                {mediaFiles.map((media, idx) => (
                  <div key={idx} style={{
                    position: 'relative',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    border: '2px solid var(--gray-300)',
                    aspectRatio: '4/3',
                    background: '#000',
                  }}>
                    {media.type === 'image' ? (
                      <img src={media.preview} alt={`Preview ${idx + 1}`} style={{
                        width: '100%', height: '100%', objectFit: 'cover',
                      }} />
                    ) : (
                      <video src={media.preview} style={{
                        width: '100%', height: '100%', objectFit: 'cover',
                      }} muted />
                    )}

                    {/* Type badge */}
                    <span style={{
                      position: 'absolute', top: '6px', left: '6px',
                      background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '0.7rem',
                      padding: '2px 8px', borderRadius: '12px', fontWeight: 600,
                    }}>
                      {media.type === 'video' ? '🎬 Video' : `📷 ${idx + 1}/${mediaFiles.length}`}
                    </span>

                    {/* Reorder buttons */}
                    <div style={{
                      position: 'absolute', bottom: '6px', left: '6px',
                      display: 'flex', gap: '4px',
                    }}>
                      {idx > 0 && (
                        <button type="button" onClick={() => handleMediaReorder(idx, idx - 1)} style={reorderBtnStyle}>
                          ◀
                        </button>
                      )}
                      {idx < mediaFiles.length - 1 && (
                        <button type="button" onClick={() => handleMediaReorder(idx, idx + 1)} style={reorderBtnStyle}>
                          ▶
                        </button>
                      )}
                    </div>

                    {/* Remove button */}
                    <button type="button" onClick={() => handleMediaRemove(idx)} style={{
                      position: 'absolute', top: '6px', right: '6px',
                      background: 'rgba(214,48,49,0.85)', color: '#fff', border: 'none',
                      borderRadius: '50%', width: '26px', height: '26px', fontSize: '0.85rem',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700,
                    }}>
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add button */}
            {mediaFiles.length < MAX_MEDIA && (
              <>
                <input
                  type="file"
                  ref={mediaInputRef}
                  onChange={handleMediaAdd}
                  accept={ACCEPTED_TYPES}
                  multiple
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => mediaInputRef.current?.click()}
                  style={{
                    width: '100%', padding: '1.2rem',
                    border: '2px dashed var(--gray-300)',
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--gray-100)', cursor: 'pointer',
                    textAlign: 'center', fontSize: '0.9rem',
                    color: 'var(--dark-light)', transition: 'all 0.3s',
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'rgba(108,92,231,0.05)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--gray-300)'; e.currentTarget.style.background = 'var(--gray-100)'; }}
                >
                  <div style={{ fontSize: '1.8rem', marginBottom: '0.3rem' }}>📷 🎬</div>
                  <div>{t('createRaffle.addMedia')}</div>
                  <div style={{ fontSize: '0.78rem', marginTop: '0.25rem', opacity: 0.7 }}>
                    {mediaFiles.length}/{MAX_MEDIA} — {t('createRaffle.mediaFormats')}
                  </div>
                </button>
              </>
            )}
          </div>

          {/* Legal Disclaimer */}
          <LegalDisclaimer accepted={legalAccepted} onAccept={setLegalAccepted} />

          {/* Submit */}
          <button
            type="submit"
            className="btn btn-primary btn-lg"
            disabled={loading || !legalAccepted}
            style={{
              width: '100%',
              marginTop: '1rem',
              opacity: legalAccepted ? 1 : 0.5,
              cursor: legalAccepted ? 'pointer' : 'not-allowed',
            }}
          >
            {loading
              ? (uploadProgress || t('payment.processing'))
              : t('raffle.create')
            }
          </button>
        </form>
      </div>
    </div>
  );
}

const reorderBtnStyle = {
  background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none',
  borderRadius: '4px', width: '24px', height: '24px', fontSize: '0.7rem',
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
